// VehicleGroupPage.tsx
import React, {FC, useCallback, useEffect, useState} from 'react'
import {Form, Formik, FormikProps} from 'formik'
import {IVehicleGroupModel, initialVehicleGroup, validationVehicleGroup} from './VehicleGroupModel'
import {createVehicleGroup, deleteVehicleGroup, loadVehicleGroups, updateVehicleGroup} from './Api'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {ToastContainer} from 'react-toastify' 
import FormTabHeader, {TabOption} from '../../../../shared/components/FormTabHeader'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import CheckboxInput from '../../../../shared/components/CheckboxInput'
import {VehicleGroupTable} from './VehicleGroupTable'
import {
  BODY_TYPES,
  FUEL_TYPES,
  TRANSMISSION_TYPES,
} from '../../../../shared/models/SharedConstantLookups'
import {currencies, segments} from '../../../../shared/models/SharedApiLookups'

const VehicleGroupPage: FC = () => {
  const [tab, setTab] = useState<string | 'form'>('form')
  const [vehicleGroups, setVehicleGroups] = useState<IVehicleGroupModel[]>([])
  const [segmentOptions, setSegmentOptions] = useState<{value: string; label: string}[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null | undefined>(null)
  const [showModal, setShowModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc') 
  const [currencyOptions, setCurrencyOptions] = useState<{value: string; label: string}[]>([])

  const tabOptions: TabOption<'form' | 'detail' | 'list'>[] = [
    {key: 'form', title: 'Araç Grubu Tanımlama', icon: 'bi bi-geo-alt'},
    {key: 'list', title: 'Araç Grubu Listesi', icon: 'bi bi-list'},
  ]

  useEffect(() => {
    Promise.all([currencies(), segments()]).then(
      ([cr, seg]) => {
        setCurrencyOptions(cr)
        setSegmentOptions(seg)
      }
    )
  }, [])

  const fetchVehicleGroups = useCallback(
    async (page: number = 0, size: number, sort?: string, order?: 'asc' | 'desc') => {
      setLoading(true) 
      try {
        const response = await loadVehicleGroups(page, size, sort, order)
        if (response.data.hasError) {
          throw new Error(response.data.message || 'Failed to fetch vehiclegroups')
        }
        setVehicleGroups(response.data.payload.content)
        setTotalPages(response.data.payload.totalPages)
      } catch (err) { 
        showErrorToast('Araç Grupları yüklenirken bir hata oluştu')
      } finally {
        setLoading(false)
      }
    },
    [pageSize]
  )

  // İlk yükleme
  useEffect(() => {
    if (tab === 'list') {
      fetchVehicleGroups(0, pageSize, sortBy, sortOrder)
    }
  }, [tab, fetchVehicleGroups])

  const handleSave = async (values: IVehicleGroupModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateVehicleGroup({...values, id: selectedId})
        showSuccessToast('Araç Grubu başarıyla güncellendi')
      } else {
        await createVehicleGroup(values)
        showSuccessToast('Araç Grubu başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchVehicleGroups(0, pageSize, sortBy, sortOrder)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = useCallback(
    (vehicleGroup: IVehicleGroupModel, formik: FormikProps<IVehicleGroupModel>) => {
      formik.setValues(vehicleGroup)
      setSelectedId(vehicleGroup.id)
      setTab('form')
    },
    []
  )

  const handleDeleteClick = useCallback((vehicleGroup: IVehicleGroupModel) => {
    setSelectedId(vehicleGroup.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId) {
      setLoading(true)
      try {
        await deleteVehicleGroup(selectedId)
        showSuccessToast('Araç Grubu başarıyla silindi')
        await fetchVehicleGroups(currentPage, pageSize, sortBy, sortOrder)
      } catch (err) {
        console.error(err)
        showErrorToast('Araç Grubu silinirken bir hata oluştu')
      } finally {
        setLoading(false)
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchVehicleGroups(newPage, pageSize, sortBy, sortOrder)
      }
    },
    [totalPages, pageSize, sortBy, sortOrder, fetchVehicleGroups]
  )

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      setPageSize(newSize)
      setCurrentPage(0)
      fetchVehicleGroups(0, newSize, sortBy, sortOrder)
    },
    [sortBy, sortOrder, fetchVehicleGroups]
  )


  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchVehicleGroups(currentPage, pageSize, column, order)
    },
    [currentPage, pageSize, fetchVehicleGroups]
  )

  return (
    <>
      <Formik
        initialValues={initialVehicleGroup}
        validationSchema={validationVehicleGroup}
        enableReinitialize={true}
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }

          return (
            <>
              <FormTabHeader
                formId='vehicleForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />

              {tab === 'form' && (
                <div className='card mb-5'>
                  <Form id='vehicleForm' className='form card-body'>
                    <div className='row mb-6'>
                      <TextInput
                        name='groupName'
                        label='Araç Grubu Adı'
                        placeholder='Araç Grubu Adı'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput name='sippCode' label='SIPP' wrapperClassName='col-md-6' />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='driverAge'
                        type='number'
                        label='Sürücü Yaşı'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='webCarBrand'
                        label='Web Araç Marka '
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='youngDriverAge'
                        type='number'
                        label='Genç Sürücü Yaşı'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='webCarType'
                        label='Web Araç Tipi '
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='licenseYear'
                        type='number'
                        label='Ehliyet Yılı'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='seatCount'
                        type='number'
                        label='Koltuk '
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='youngLicenseYear'
                        type='number'
                        label='Genç Ehliyet Yılı'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='doorCount'
                        type='number'
                        label='Kapı Sayısı '
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='provisionFee'
                        currency={true}
                        label='Provizyon Ücreti'
                        wrapperClassName='col-md-3'
                      />
                      <SelectInput
                        name='provisionFeeCurrency'
                        label='Para Birimi'
                        wrapperClassName='col-md-3'
                        options={currencyOptions}
                      />
                      <TextInput
                        name='bigTrunkCount'
                        type='number'
                        label='Büyük Bagaj '
                        wrapperClassName='col-md-6'
                      />
                    </div>

                    <div className='row mb-6'>
                      <TextInput
                        name='exemptionFee'
                        currency={true}
                        label='Muafiyet Ücreti'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='smallTrunkCount'
                        type='number'
                        label='Küçük Bagaj '
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='fuelPrice'
                        currency={true}
                        label='Yakıt Fiyatı'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput name='description' label='Açıklama ' wrapperClassName='col-md-6' />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='dailyKmLimit'
                        type='number'
                        label='Günlük KM Limiti'
                        wrapperClassName='col-md-6'
                      />
                      <SelectInput
                        name='fuelType'
                        label='Yakıt Türü '
                        wrapperClassName='col-md-6'
                        options={FUEL_TYPES}
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='maxKmLimit'
                        type='number'
                        label='MAX KM Limiti'
                        wrapperClassName='col-md-6'
                      />
                      <SelectInput
                        name='transmission'
                        label='Vites '
                        wrapperClassName='col-md-6'
                        options={TRANSMISSION_TYPES}
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='kmFee'
                        currency={true}
                        label='KM Fiyatı'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='postPayRate'
                        type='number'
                        label='Sonra Öde Fiyat Oranı(Özel Web Site Tasarımları için) '
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='webSort'
                        type='number'
                        label='Web Sıra'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='specialCode'
                        type='number'
                        label='Özel Kod '
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='webId'
                        type='number'
                        label='Web ID '
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='upgradeSort'
                        type='number'
                        label='Upgrate Sıra'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='serviceId'
                        type='number'
                        label='Servis ID'
                        wrapperClassName='col-md-6'
                      />
                      <SelectInput
                        name='bodyType'
                        label='Kasa Türü'
                        wrapperClassName='col-md-6'
                        options={BODY_TYPES}
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput
                        name='carGroupCode'
                        type='number'
                        label='Araç Grubu Kodu'
                        wrapperClassName='col-md-6'
                      />
                      <SelectInput
                        name='segmentId'
                        label='Segment'
                        wrapperClassName='col-md-6'
                        options={segmentOptions}
                      />
                    </div>
                    <div className='row mb-6'>
                      <CheckboxInput
                        name='isPassive'
                        label='Pasif Et'
                        wrapperClassName='col-md-3'
                      />
                      <CheckboxInput
                        name='creditCardRequired'
                        label='Kredi Kartı Şartı'
                        wrapperClassName='col-md-3'
                      />
                    </div>
                  </Form>
                </div>
              )}
              {tab === 'list' && (
                <div className='card'>
                  <div className='card-body pt-0'>
                    <VehicleGroupTable
                      data={vehicleGroups}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      pageSize={pageSize}
                      onPageSizeChange={(size) => handlePageSizeChange(size)}
                      onPageChange={handlePageChange}
                      onEdit={(vehicleGroup) => handleEdit(vehicleGroup, formik)}
                      onDelete={handleDeleteClick}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu Araç Grubunu silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export {VehicleGroupPage}
